class TabsElement extends HTMLElement {
  /** @type {HTMLButtonElement[]} */ #tabs;
  /** @type {HTMLElement[]} */ #panels;

  /** @type {Record<string, (index: number, lastIndex: number) => number>} */
  #keyMap = {
    ArrowLeft: (i, last) => (i > 0 ? i - 1 : last),
    ArrowRight: (i, last) => (i < last ? i + 1 : 0),
    Home: () => 0,
    End: (_i, last) => last,
  };

  connectedCallback() {
    this.#tabs = [...this.querySelectorAll('[role="tab"]')];
    this.#panels = this.#tabs.map((tab) =>
      document.getElementById(tab.getAttribute('aria-controls'))
    );

    if (!this.#tabs.length || !this.#panels.length) return;

    this.addEventListener('click', (event) => this.#handleClick(event));
    this.addEventListener('keydown', (event) => this.#handleKeydown(event));
    document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event));
  }

  /** Handles click events on tabs.
   * @param {MouseEvent} event */
  #handleClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.#tabs.includes(tab)) this.#selectTab(tab);
  }

  /** Handles keyboard navigation.
   * @param {KeyboardEvent} event */
  #handleKeydown(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.#tabs.includes(tab)) return;

    const resolver = this.#keyMap[event.key];
    if (!resolver) return;

    event.preventDefault();
    const index = this.#tabs.indexOf(tab);
    this.#selectTab(this.#tabs[resolver(index, this.#tabs.length - 1)]);
  }

  /** Selects a tab and updates the UI.
   * @param {HTMLElement} selectedTab */
  #selectTab(selectedTab) {
    this.#tabs.forEach((tab, i) => {
      const selected = tab === selectedTab;
      const panel = this.#panels[i];
      if (!panel) {
        console.warn(`[TabsElement] Panel not found for tab ${tab.id}`);
        return;
      }
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      panel.hidden = !selected;
    });

    selectedTab.focus();
  }

  /** Navigates to a tab if it includes a selected Shopify block.
   * @param {Event} event */
  #onBlockSelect(event) {
    const panel = event.target.closest('[role="tabpanel"]');
    if (!panel) return;
    const tab = this.#tabs.find((tab) => tab.getAttribute('aria-controls') === panel.id);
    if (tab) this.#selectTab(tab);
  }
}

if (!customElements.get('tabs-element')) {
  customElements.define('tabs-element', TabsElement);
}
